// TelcoTrack — ROZVADEC cabinet + material identity catalog.
// Typ skříně se odvozuje z identity SAP v `materialUsed` (ne z pickeru).
// Legacy `ROZVADEC_TYPES` v `component.constants.ts` se sem nemapuje.

import type { RozvadecCassetteTypeId } from './rozvadec-cassette.constants';
import {
  attachRozvadecIdentityCapacity,
  resolveRozvadecLiveWeldCapacity,
  type RozvadecWeldCapacityProfile,
} from './rozvadec-weld-capacity';

export const ROZVADEC_CONNECTION_KINDS = ['direct', 'connector', 'splitter'] as const;
export type RozvadecConnectionKind = (typeof ROZVADEC_CONNECTION_KINDS)[number];

export const ROZVADEC_CABINET_KINDS = ['address', 'technology'] as const;
export type RozvadecCabinetKind = (typeof ROZVADEC_CABINET_KINDS)[number];

export const ROZVADEC_CABINET_TYPE_IDS = [
  'PON16',
  'PON32',
  'MUK_1X24',
  'MUK_2X24',
  'OS',
  'SDF_384',
  'SDF_432',
  'RSU_ODF',
] as const;
export type RozvadecCabinetTypeId = (typeof ROZVADEC_CABINET_TYPE_IDS)[number];

export const ROZVADEC_SPLITTER_RATIOS = ['1:4', '1:8', '1:16', '1:32', '1:64'] as const;
export type RozvadecSplitterRatio = (typeof ROZVADEC_SPLITTER_RATIOS)[number];

const ADDRESS_CONNECTION_KINDS = ['direct', 'connector', 'splitter'] as const satisfies readonly RozvadecConnectionKind[];
const OS_CONNECTION_KINDS = ['direct'] as const satisfies readonly RozvadecConnectionKind[];

export interface RozvadecCabinetSection {
  id: string;
  nazev?: string;
  kapacita: number;
  sloupce: number;
  radky: number;
}

/** Direction in which the section is numbered and filled (PI-0056). */
export const ROZVADEC_CASSETTE_FILL_DIRECTIONS = ['bottom', 'top'] as const;
export type RozvadecCassetteFillDirection = (typeof ROZVADEC_CASSETTE_FILL_DIRECTIONS)[number];

/**
 * One vertical column of cassette positions. Position 0 is the first position in the
 * section's numbering order (`fillFrom: 'bottom'` numbers from the bottom, `'top'`
 * from the top). MUK and PON32 columns use `'top'` (physical 1 at the top). Span-2 cassettes
 * occupy `position` and `position + 1`. Do not reuse this helper for RAY-TR3 (U1
 * is still the bottom of the 42U frame).
 */
export interface RozvadecCabinetCassetteSection {
  id: string;
  nazev?: string;
  positions: number;
  fillFrom: RozvadecCassetteFillDirection;
}

export interface RozvadecCabinetCassetteRack {
  sections: readonly RozvadecCabinetCassetteSection[];
  allowedCassetteTypes: readonly RozvadecCassetteTypeId[];
  /** PON32 per PI-0045: PLC splitters are stored outside cassettes (loose terminations). */
  splittersOutsideCassettes: boolean;
}

/**
 * Jedna identita materiálu. Kapacita je `capacity` (included / expandable / unit / mode).
 * `maxWeldCount` je jen derived: `capacityMode === 'fixed'` + jednoznačná weld unit; jinak null.
 * Shodné SAP nikdy nesmí mapovat na dva `typeId`.
 */
export interface RozvadecMaterialIdentity {
  sapCode: string;
  typeId: RozvadecCabinetTypeId;
  nazev: string;
  maxWeldCount: number | null;
  capacity: RozvadecWeldCapacityProfile;
}

export interface RozvadecCabinetTypeDef {
  id: RozvadecCabinetTypeId;
  nazev: string;
  kind: RozvadecCabinetKind;
  allowedConnectionKinds: readonly RozvadecConnectionKind[];
  /** Import / rozpoznání z podkladů — ne UI picker. */
  aliases: readonly string[];
  kapacita: number;
  sekce: readonly RozvadecCabinetSection[];
  /** Kazetový rack (weld-layout v2). Null = terminations are loose (no cassettes). */
  cassetteRack: RozvadecCabinetCassetteRack | null;
  /** Odvozeno z katalogu identit — needitovat samostatně. */
  identitySapCodes: readonly string[];
}

/**
 * Minimální tvar spotřeby, který helpery berou.
 * Commit `materialUsed`: `{ sapCode, quantity?, label? }`.
 * Usage row / historie: `{ sapCode, name?, quantity? }` (+ volitelné `materialCode`).
 */
export type RozvadecMaterialUsageLike = {
  sapCode?: string | null;
  materialCode?: string | null;
  name?: string | null;
  label?: string | null;
  quantity?: number | null;
};

export type RozvadecCabinetMaterialMatch = {
  typeId: RozvadecCabinetTypeId;
  type: RozvadecCabinetTypeDef;
  identity: RozvadecMaterialIdentity | null;
  matchedBy: 'sap' | 'alias';
  sapCode?: string;
};

export type RozvadecCabinetTypeResolveResult =
  | { status: 'none'; matches: readonly RozvadecCabinetMaterialMatch[] }
  | { status: 'resolved'; type: RozvadecCabinetTypeDef; matches: readonly RozvadecCabinetMaterialMatch[] }
  | {
      status: 'conflict';
      typeIds: readonly RozvadecCabinetTypeId[];
      matches: readonly RozvadecCabinetMaterialMatch[];
    };

export type RozvadecCabinetGrid = {
  typeId: RozvadecCabinetTypeId;
  kapacita: number;
  sekce: readonly RozvadecCabinetSection[];
};

export type RozvadecCabinetGridCell = {
  sectionId: string;
  sectionIndex: number;
  slotIndex: number;
  row: number;
  col: number;
  globalIndex: number;
};

export type RozvadecCabinetMaterialConflict = {
  typeIds: readonly RozvadecCabinetTypeId[];
  matches: readonly RozvadecCabinetMaterialMatch[];
};

/** Svařitelné prvky mimo identity katalog — typ neurčují. */
export const SPLICE_CAPABLE_CANDIDATE_SAP_CODES = [
  '325851',
  '328596',
  '313802',
  '319718',
  '319634',
  '327066',
  '323176',
  '323177',
] as const;

const CABINET_CONNECTIONS = ADDRESS_CONNECTION_KINDS;

/**
 * Schválené identity SAP (20 OS + PON32 + MUK + RAY-TR3).
 * Weld numbers live in `rozvadec-weld-capacity.ts` — do not put TENIO catalog-max
 * into `maxWeldCount` (325760 expandable is 288, included 96).
 */
const ROZVADEC_MATERIAL_IDENTITY_ROWS = [
  { sapCode: '324363', typeId: 'PON32' as const, nazev: 'Skříň PON 32 – sloupek' },
  { sapCode: '324362', typeId: 'PON32' as const, nazev: 'Skříň PON 32 – zazdívací' },
  { sapCode: '326274', typeId: 'MUK_1X24' as const, nazev: 'Kabinet 1×24 universální (MUK)' },
  { sapCode: '326273', typeId: 'MUK_2X24' as const, nazev: 'Kabinet 2×24 universální (MUK)' },
  { sapCode: '325715', typeId: 'RSU_ODF' as const, nazev: 'Rozvaděč opt. RAY-TR3 sestava' },
  {
    sapCode: '325701',
    typeId: 'RSU_ODF' as const,
    nazev: 'Rozváděč opt. RAY-TR3 2200 x 900 x 300mm',
  },
  { sapCode: '325739', typeId: 'OS' as const, nazev: 'BPEO-15-RB dělicí' },
  { sapCode: '327337', typeId: 'OS' as const, nazev: 'BPEO 1,5 drop – tělo' },
  { sapCode: '325737', typeId: 'OS' as const, nazev: 'BPEO-15-CN' },
  { sapCode: '319647', typeId: 'OS' as const, nazev: 'COYOTE Drop Terminal Closure (DTC)' },
  { sapCode: '320184', typeId: 'OS' as const, nazev: 'COYOTE Short Dome 9,5×19, ribbon' },
  { sapCode: '320183', typeId: 'OS' as const, nazev: 'COYOTE Dome 6,5×17, ribbon' },
  { sapCode: '318524', typeId: 'OS' as const, nazev: 'COYOTE Dome 9,5×28' },
  { sapCode: '306732', typeId: 'OS' as const, nazev: 'COYOTE RUNT In-Line' },
  { sapCode: '325532', typeId: 'OS' as const, nazev: 'COYOTE SFMS sestava, splitter 1:16' },
  { sapCode: '324995', typeId: 'OS' as const, nazev: 'COYOTE SFMS sestava, splitter 1:32' },
  { sapCode: '324994', typeId: 'OS' as const, nazev: 'COYOTE SFMS sestava, splitter 1:64' },
  { sapCode: '325764', typeId: 'OS' as const, nazev: 'STP-XL distribuční malá' },
  { sapCode: '325765', typeId: 'OS' as const, nazev: 'STP-XL distribuční velká' },
  { sapCode: '325766', typeId: 'OS' as const, nazev: 'STP-XL s konektorovým polem' },
  { sapCode: '325767', typeId: 'OS' as const, nazev: 'STP-XL dělicí pro LT' },
  { sapCode: '325768', typeId: 'OS' as const, nazev: 'STP-XL dělicí pro ribbon' },
  { sapCode: '325736', typeId: 'OS' as const, nazev: 'TENIO-B6-DS malá' },
  { sapCode: '325760', typeId: 'OS' as const, nazev: 'TENIO-C6H-DL malá' },
  { sapCode: '325735', typeId: 'OS' as const, nazev: 'TENIO-C6H-DL velká' },
  { sapCode: '325738', typeId: 'OS' as const, nazev: 'TENIO-C6H-LT dělicí' },
] as const;

export const ROZVADEC_MATERIAL_IDENTITIES: readonly RozvadecMaterialIdentity[] =
  ROZVADEC_MATERIAL_IDENTITY_ROWS.map((row) => attachRozvadecIdentityCapacity(row));

function identitySapCodesFor(typeId: RozvadecCabinetTypeId): readonly string[] {
  return ROZVADEC_MATERIAL_IDENTITIES.filter((row) => row.typeId === typeId).map((row) => row.sapCode);
}

/** MUK racks accept the full MICOS cassette family (PI-0053/PI-0056). */
const MUK_CASSETTE_TYPES = [
  'KM4',
  'KM5_SC',
  'KM5_LC',
  'KM5_SPLITTER_1X16',
  'KM5_SPLITTER_2X8',
  'KM5_SPLITTER_1X4',
  'KM5_SPLITTER_2X4',
] as const satisfies readonly RozvadecCassetteTypeId[];

/** PON32 ships with 4 KM6 tray positions; splitters live outside cassettes (PI-0045). */
const PON32_CASSETTE_TYPES = ['KM6'] as const satisfies readonly RozvadecCassetteTypeId[];

export const ROZVADEC_CABINET_TYPES: readonly RozvadecCabinetTypeDef[] = [
  {
    id: 'PON16',
    nazev: 'PON 16',
    kind: 'address',
    allowedConnectionKinds: CABINET_CONNECTIONS,
    aliases: ['PON16', 'PON 16', 'DP/PON16'],
    kapacita: 16,
    sekce: [{ id: 'sekce_1', kapacita: 16, sloupce: 4, radky: 4 }],
    cassetteRack: null,
    identitySapCodes: identitySapCodesFor('PON16'),
  },
  {
    id: 'PON32',
    nazev: 'PON 32',
    kind: 'address',
    allowedConnectionKinds: CABINET_CONNECTIONS,
    aliases: ['PON32', 'PON 32', 'DP/PON32'],
    kapacita: 32,
    sekce: [{ id: 'sekce_1', kapacita: 32, sloupce: 8, radky: 4 }],
    cassetteRack: {
      sections: [{ id: 'rack', nazev: 'Rack', positions: 4, fillFrom: 'top' }],
      allowedCassetteTypes: PON32_CASSETTE_TYPES,
      splittersOutsideCassettes: true,
    },
    identitySapCodes: identitySapCodesFor('PON32'),
  },
  {
    id: 'MUK_1X24',
    nazev: 'MUK 1×24',
    kind: 'address',
    allowedConnectionKinds: CABINET_CONNECTIONS,
    aliases: ['MUK 1×24', 'MUK 1x24', 'Kabinet 1x24 universální', 'Kabinet 1×24 universální'],
    kapacita: 24,
    sekce: [{ id: 'sekce_1', kapacita: 24, sloupce: 6, radky: 4 }],
    cassetteRack: {
      sections: [{ id: 'rada_1', nazev: 'Řada', positions: 24, fillFrom: 'top' }],
      allowedCassetteTypes: MUK_CASSETTE_TYPES,
      splittersOutsideCassettes: false,
    },
    identitySapCodes: identitySapCodesFor('MUK_1X24'),
  },
  {
    id: 'MUK_2X24',
    nazev: 'MUK 2×24',
    kind: 'address',
    allowedConnectionKinds: CABINET_CONNECTIONS,
    aliases: ['MUK48', 'MUK 48', 'MUK2x24', 'MUK 2×24', 'MUK 2x24', 'Kabinet 2×24 universální'],
    kapacita: 48,
    sekce: [
      { id: 'rada_1', nazev: '1. řada', kapacita: 24, sloupce: 6, radky: 4 },
      { id: 'rada_2', nazev: '2. řada', kapacita: 24, sloupce: 6, radky: 4 },
    ],
    cassetteRack: {
      sections: [
        { id: 'rada_1', nazev: '1. řada', positions: 24, fillFrom: 'top' },
        { id: 'rada_2', nazev: '2. řada', positions: 24, fillFrom: 'top' },
      ],
      allowedCassetteTypes: MUK_CASSETTE_TYPES,
      splittersOutsideCassettes: false,
    },
    identitySapCodes: identitySapCodesFor('MUK_2X24'),
  },
  {
    id: 'OS',
    nazev: 'Optická spojka',
    kind: 'technology',
    allowedConnectionKinds: OS_CONNECTION_KINDS,
    aliases: ['OS', 'optická spojka', 'opticka spojka'],
    kapacita: 0,
    sekce: [],
    cassetteRack: null,
    identitySapCodes: identitySapCodesFor('OS'),
  },
  {
    id: 'SDF_384',
    nazev: 'SDF 384',
    kind: 'technology',
    allowedConnectionKinds: CABINET_CONNECTIONS,
    aliases: ['SDF 384', 'SDF384'],
    /** Fiber / connector field size — not a weld count. */
    kapacita: 384,
    sekce: [],
    cassetteRack: null,
    identitySapCodes: identitySapCodesFor('SDF_384'),
  },
  {
    id: 'SDF_432',
    nazev: 'SDF 432',
    kind: 'technology',
    allowedConnectionKinds: CABINET_CONNECTIONS,
    aliases: ['SDF 432', 'SDF432', 'SDF 384(432)'],
    /** Fiber / connector field size — not a weld count. */
    kapacita: 432,
    sekce: [],
    cassetteRack: null,
    identitySapCodes: identitySapCodesFor('SDF_432'),
  },
  {
    id: 'RSU_ODF',
    nazev: 'RSU / ODF',
    kind: 'technology',
    allowedConnectionKinds: CABINET_CONNECTIONS,
    aliases: ['RAY-TR3', 'RSU_ODF'],
    kapacita: 0,
    sekce: [],
    cassetteRack: null,
    identitySapCodes: identitySapCodesFor('RSU_ODF'),
  },
];

/** Ambiguous documentary labels — never auto-map without SAP. */
const AMBIGUOUS_CABINET_ALIAS_KEYS = new Set([
  normalizeCabinetAliasKey('MUK24'),
  normalizeCabinetAliasKey('MUK 24'),
  normalizeCabinetAliasKey('MUK'),
  normalizeCabinetAliasKey('PON'),
  normalizeCabinetAliasKey('RSU'),
]);

const TYPE_BY_ID: Record<RozvadecCabinetTypeId, RozvadecCabinetTypeDef> = ROZVADEC_CABINET_TYPES.reduce(
  (acc, def) => {
    acc[def.id] = def;
    return acc;
  },
  {} as Record<RozvadecCabinetTypeId, RozvadecCabinetTypeDef>,
);

const IDENTITY_BY_SAP = new Map<string, RozvadecMaterialIdentity>(
  ROZVADEC_MATERIAL_IDENTITIES.map((row) => [row.sapCode, row]),
);

const TYPE_BY_ALIAS_KEY = buildAliasLookup();

function normalizeCabinetAliasKey(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[×✕✖⨯]/g, 'x')
    .replace(/[^a-z0-9]+/g, '');
}

function buildAliasLookup(): Map<string, RozvadecCabinetTypeId> {
  const map = new Map<string, RozvadecCabinetTypeId>();
  const claimed = new Map<string, RozvadecCabinetTypeId>();
  for (const def of ROZVADEC_CABINET_TYPES) {
    const labels = [def.id, def.nazev, ...def.aliases];
    for (const label of labels) {
      const key = normalizeCabinetAliasKey(label);
      if (!key || AMBIGUOUS_CABINET_ALIAS_KEYS.has(key)) continue;
      const existing = claimed.get(key);
      if (existing && existing !== def.id) {
        map.delete(key);
        continue;
      }
      claimed.set(key, def.id);
      map.set(key, def.id);
    }
  }
  for (const blocked of AMBIGUOUS_CABINET_ALIAS_KEYS) {
    map.delete(blocked);
  }
  return map;
}

export function isRozvadecCabinetTypeId(value: string | null | undefined): value is RozvadecCabinetTypeId {
  return (ROZVADEC_CABINET_TYPE_IDS as readonly string[]).includes((value ?? '').trim());
}

export function isRozvadecConnectionKind(value: string | null | undefined): value is RozvadecConnectionKind {
  return (ROZVADEC_CONNECTION_KINDS as readonly string[]).includes((value ?? '').trim());
}

export function getRozvadecCabinetType(id: string | null | undefined): RozvadecCabinetTypeDef | undefined {
  const key = (id ?? '').trim();
  if (!isRozvadecCabinetTypeId(key)) return undefined;
  return TYPE_BY_ID[key];
}

export function getRozvadecMaterialIdentity(sapCode: string | null | undefined): RozvadecMaterialIdentity | undefined {
  const sap = normalizeRozvadecSapCode(sapCode);
  return sap ? IDENTITY_BY_SAP.get(sap) : undefined;
}

export function normalizeRozvadecSapCode(raw: string | null | undefined): string {
  return String(raw ?? '').trim();
}

export function resolveRozvadecCabinetTypeByAlias(text: string | null | undefined): RozvadecCabinetTypeDef | undefined {
  const key = normalizeCabinetAliasKey(text ?? '');
  if (!key || AMBIGUOUS_CABINET_ALIAS_KEYS.has(key)) return undefined;
  const typeId = TYPE_BY_ALIAS_KEY.get(key);
  return typeId ? TYPE_BY_ID[typeId] : undefined;
}

export function splitterRatioOutputCount(ratio: string | null | undefined): number | null {
  const raw = (ratio ?? '').trim();
  if (!(ROZVADEC_SPLITTER_RATIOS as readonly string[]).includes(raw)) return null;
  const parts = raw.split(':');
  const n = Number(parts[1]);
  return Number.isInteger(n) && n > 0 ? n : null;
}

function isConsumedMaterialRow(row: RozvadecMaterialUsageLike): boolean {
  if (row.quantity == null) return true;
  return Number.isFinite(row.quantity) && row.quantity > 0;
}

function rowSapCode(row: RozvadecMaterialUsageLike): string {
  return normalizeRozvadecSapCode(row.sapCode) || normalizeRozvadecSapCode(row.materialCode);
}

function rowAliasText(row: RozvadecMaterialUsageLike): string {
  return String(row.name ?? row.label ?? '').trim();
}

export function collectRozvadecCabinetMaterialMatches(
  used: readonly RozvadecMaterialUsageLike[] | null | undefined,
): RozvadecCabinetMaterialMatch[] {
  if (!Array.isArray(used) || used.length === 0) return [];
  const matches: RozvadecCabinetMaterialMatch[] = [];
  const seen = new Set<string>();
  for (const row of used) {
    if (!row || !isConsumedMaterialRow(row)) continue;
    const sap = rowSapCode(row);
    const identity = sap ? IDENTITY_BY_SAP.get(sap) : undefined;
    if (identity) {
      const type = TYPE_BY_ID[identity.typeId];
      const key = `sap:${identity.sapCode}`;
      if (seen.has(key)) continue;
      seen.add(key);
      matches.push({ typeId: identity.typeId, type, identity, matchedBy: 'sap', sapCode: identity.sapCode });
      continue;
    }
    const type = resolveRozvadecCabinetTypeByAlias(rowAliasText(row));
    if (!type) continue;
    const key = `alias:${type.id}:${rowAliasText(row)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    matches.push({ typeId: type.id, type, identity: null, matchedBy: 'alias' });
  }
  return matches;
}

function uniqueTypeIds(matches: readonly RozvadecCabinetMaterialMatch[]): RozvadecCabinetTypeId[] {
  const ids: RozvadecCabinetTypeId[] = [];
  const seen = new Set<string>();
  for (const match of matches) {
    if (seen.has(match.typeId)) continue;
    seen.add(match.typeId);
    ids.push(match.typeId);
  }
  return ids;
}

export function resolveRozvadecCabinetTypeFromMaterials(
  used: readonly RozvadecMaterialUsageLike[] | null | undefined,
): RozvadecCabinetTypeResolveResult {
  const matches = collectRozvadecCabinetMaterialMatches(used);
  const typeIds = uniqueTypeIds(matches);
  if (typeIds.length === 0) return { status: 'none', matches };
  if (typeIds.length > 1) return { status: 'conflict', typeIds, matches };
  return { status: 'resolved', type: TYPE_BY_ID[typeIds[0]], matches };
}

/** True when at least one identity SAP is present (name-only alias does not satisfy Postavený). */
export function hasRequiredRozvadecCabinetMaterial(
  used: readonly RozvadecMaterialUsageLike[] | null | undefined,
): boolean {
  return collectRozvadecCabinetMaterialMatches(used).some((match) => match.matchedBy === 'sap');
}

export function findRozvadecCabinetMaterialConflict(
  used: readonly RozvadecMaterialUsageLike[] | null | undefined,
): RozvadecCabinetMaterialConflict | null {
  const resolved = resolveRozvadecCabinetTypeFromMaterials(used);
  if (resolved.status !== 'conflict') return null;
  return { typeIds: resolved.typeIds, matches: resolved.matches };
}

export function isRozvadecConnectionKindAllowed(
  typeId: string | null | undefined,
  kind: string | null | undefined,
): boolean {
  const type = getRozvadecCabinetType(typeId);
  const connection = (kind ?? '').trim();
  if (!type || !isRozvadecConnectionKind(connection)) return false;
  return type.allowedConnectionKinds.includes(connection);
}

export function resolveRozvadecMaxWeldCountFromMaterials(
  used: readonly RozvadecMaterialUsageLike[] | null | undefined,
): number | null {
  const known = collectRozvadecCabinetMaterialMatches(used)
    .map((match) => match.identity?.maxWeldCount)
    .filter((n): n is number => n != null && Number.isFinite(n) && n > 0);
  if (known.length === 0) return null;
  return Math.min(...known);
}

/** Live weld ceiling (TENIO expandable, BPEO trays, MUK/PON osazení). Fixed-only `maxWeldCount` is narrower. */
export function resolveRozvadecWeldLimitFromMaterials(
  used: readonly RozvadecMaterialUsageLike[] | null | undefined,
  options?: { typeId?: string | null; cassettes?: readonly { cassetteType: string }[] | null },
): number | null {
  return resolveRozvadecLiveWeldCapacity({
    typeId: options?.typeId,
    materials: used,
    cassettes: options?.cassettes,
  }).weldLimit;
}

export type RozvadecConnectionCapabilityFamily = 'cabinet' | 'os' | 'unknown' | 'conflict';

export type RozvadecResolvedConnectionCapabilities = {
  family: RozvadecConnectionCapabilityFamily;
  typeId: RozvadecCabinetTypeId | null;
  typeKind: RozvadecCabinetKind | null;
  allowedKinds: readonly RozvadecConnectionKind[];
  /** Derived fixed-mode weld cap only. Prefer `weldLimit` for enforcement. */
  maxWeldCount: number | null;
  /** Live weld ceiling from profiles + osazení. */
  weldLimit: number | null;
  connectorLimit: number | null;
  /**
   * @deprecated Type `kapacita` is not a weld count (SDF 384/432 = fiber field, RAY-TR3 ≠ 144).
   * Always null — use `weldLimit` / `connectorLimit`.
   */
  catalogWeldCapacity: number | null;
  /** True when the cabinet is OS and no approved / computable weld ceiling exists. */
  unknownOsLimit: boolean;
  identitySapCodes: string[];
};

export type RozvadecCapabilityResolveInput = {
  persistedTypeId?: string | null;
  derivedFromSap?: string | null;
  materials?: readonly RozvadecMaterialUsageLike[] | null;
};

function uniqueIdentitySapCodes(matches: readonly RozvadecCabinetMaterialMatch[]): string[] {
  return [
    ...new Set(
      matches
        .map((match) => match.sapCode)
        .filter((sap): sap is string => Boolean(sap)),
    ),
  ];
}

function collectOsIdentities(input: RozvadecCapabilityResolveInput): RozvadecMaterialIdentity[] {
  const identities: RozvadecMaterialIdentity[] = [];
  const seen = new Set<string>();
  for (const match of collectRozvadecCabinetMaterialMatches(input.materials)) {
    if (match.identity?.typeId !== 'OS') continue;
    if (seen.has(match.identity.sapCode)) continue;
    seen.add(match.identity.sapCode);
    identities.push(match.identity);
  }
  const derived = getRozvadecMaterialIdentity(input.derivedFromSap);
  if (derived?.typeId === 'OS' && !seen.has(derived.sapCode)) {
    identities.push(derived);
  }
  return identities;
}

/**
 * OS weld cap from persisted SAP first, then material identities.
 * Missing / unapproved OS SAP is `unknownOsLimit` — callers must not invent a cap.
 * `maxWeldCount` here is the live weld limit (TENIO expandable, STP included, BPEO trays).
 */
export function resolveRozvadecOsWeldLimit(
  input: RozvadecCapabilityResolveInput,
): { maxWeldCount: number | null; unknownOsLimit: boolean; weldLimit: number | null } {
  const persisted = getRozvadecCabinetType(input.persistedTypeId);
  if (persisted && persisted.id !== 'OS') {
    return { maxWeldCount: null, unknownOsLimit: false, weldLimit: null };
  }
  const fromMaterials = resolveRozvadecCabinetTypeFromMaterials(input.materials);
  const isOs =
    persisted?.id === 'OS' ||
    (fromMaterials.status === 'resolved' && fromMaterials.type.id === 'OS');
  if (!isOs) {
    return { maxWeldCount: null, unknownOsLimit: false, weldLimit: null };
  }
  const identities = collectOsIdentities(input);
  if (identities.length === 0) {
    return { maxWeldCount: null, unknownOsLimit: true, weldLimit: null };
  }
  const live = resolveRozvadecLiveWeldCapacity({
    typeId: 'OS',
    derivedFromSap: input.derivedFromSap,
    materials: input.materials,
  });
  return {
    maxWeldCount: live.weldLimit,
    weldLimit: live.weldLimit,
    unknownOsLimit: live.unknownOsLimit,
  };
}

/**
 * Type `kapacita` is not a weld count. Always null.
 * SDF 384/432 = fiber/connector field; RAY-TR3 has no inherent 144 welds.
 */
export function rozvadecTechnologyCatalogWeldCapacity(
  _typeId: string | null | undefined,
): number | null {
  return null;
}

export function isRozvadecTechnologyCatalogCapacityExceeded(
  typeId: string | null | undefined,
  weldCount: number,
): boolean {
  const capacity = rozvadecTechnologyCatalogWeldCapacity(typeId);
  if (capacity == null || !Number.isFinite(weldCount)) return false;
  return weldCount > capacity;
}

function capabilitiesForType(
  type: RozvadecCabinetTypeDef,
  input: RozvadecCapabilityResolveInput,
  identitySapCodes: string[],
): RozvadecResolvedConnectionCapabilities {
  const live = resolveRozvadecLiveWeldCapacity({
    typeId: type.id,
    derivedFromSap: input.derivedFromSap,
    materials: input.materials,
  });
  const osLimit = resolveRozvadecOsWeldLimit({
    persistedTypeId: type.id,
    derivedFromSap: input.derivedFromSap,
    materials: input.materials,
  });
  return {
    family: type.id === 'OS' ? 'os' : 'cabinet',
    typeId: type.id,
    typeKind: type.kind,
    allowedKinds: type.allowedConnectionKinds,
    maxWeldCount: live.maxWeldCount,
    weldLimit: type.id === 'OS' ? osLimit.weldLimit : live.weldLimit,
    connectorLimit: live.connectorLimit,
    catalogWeldCapacity: null,
    unknownOsLimit: osLimit.unknownOsLimit,
    identitySapCodes,
  };
}

/**
 * Connection capabilities: persisted `rozvadec_spec.typeId` wins.
 * Material catalog is fallback only when no type is stored (legacy cabinets).
 * Untyped cabinets accept direct welds only — same as the safe UI contract.
 */
export function resolveRozvadecConnectionCapabilities(
  input: RozvadecCapabilityResolveInput,
): RozvadecResolvedConnectionCapabilities {
  const persisted = getRozvadecCabinetType(input.persistedTypeId);
  if (persisted) {
    return capabilitiesForType(persisted, input, [...persisted.identitySapCodes]);
  }

  const resolved = resolveRozvadecCabinetTypeFromMaterials(input.materials);
  if (resolved.status === 'conflict') {
    return {
      family: 'conflict',
      typeId: null,
      typeKind: null,
      allowedKinds: [],
      maxWeldCount: null,
      weldLimit: null,
      connectorLimit: null,
      catalogWeldCapacity: null,
      unknownOsLimit: false,
      identitySapCodes: uniqueIdentitySapCodes(resolved.matches),
    };
  }
  if (resolved.status === 'resolved') {
    return capabilitiesForType(
      resolved.type,
      {
        persistedTypeId: resolved.type.id,
        derivedFromSap: input.derivedFromSap,
        materials: input.materials,
      },
      uniqueIdentitySapCodes(resolved.matches),
    );
  }
  return {
    family: 'unknown',
    typeId: null,
    typeKind: null,
    allowedKinds: OS_CONNECTION_KINDS,
    maxWeldCount: null,
    weldLimit: null,
    connectorLimit: null,
    catalogWeldCapacity: null,
    unknownOsLimit: false,
    identitySapCodes: [],
  };
}

/**
 * Persist path: typeId only from a real identity SAP. Alias hits stay display-only.
 */
export function resolveRozvadecPersistableTypeFromMaterials(
  used: readonly RozvadecMaterialUsageLike[] | null | undefined,
): RozvadecCabinetTypeResolveResult {
  const sapMatches = collectRozvadecCabinetMaterialMatches(used).filter(
    (match) => match.matchedBy === 'sap',
  );
  const typeIds = uniqueTypeIds(sapMatches);
  if (typeIds.length === 0) return { status: 'none', matches: sapMatches };
  if (typeIds.length > 1) return { status: 'conflict', typeIds, matches: sapMatches };
  return { status: 'resolved', type: TYPE_BY_ID[typeIds[0]], matches: sapMatches };
}

/** Address cabinets: assigned branches. Technology (OS/SDF/RSU): also unassigned directs. */
export function secondaryWeldCountForCabinetType(
  typeId: string | null | undefined,
  assignedBranchCount: number,
  unassignedDirectCount: number,
): number {
  const type = getRozvadecCabinetType(typeId);
  if (type?.kind === 'technology') {
    return assignedBranchCount + unassignedDirectCount;
  }
  return assignedBranchCount;
}

/**
 * OS without a computable weld ceiling cannot be marked complete.
 * PON/MUK/RSU without live osazení have no material weld constant — returns true.
 */
export function isRozvadecWeldCountWithinKnownLimit(
  used: readonly RozvadecMaterialUsageLike[] | null | undefined,
  weldCount: number,
): boolean {
  if (!Number.isFinite(weldCount) || weldCount < 0) return false;
  const osIdentities = collectRozvadecCabinetMaterialMatches(used)
    .map((match) => match.identity)
    .filter((identity): identity is RozvadecMaterialIdentity => identity?.typeId === 'OS');
  if (osIdentities.length === 0) return true;
  const live = resolveRozvadecLiveWeldCapacity({ typeId: 'OS', materials: used });
  if (live.unknownOsLimit || live.weldLimit == null) return false;
  return weldCount <= live.weldLimit;
}

export function cabinetGridForType(id: string | null | undefined): RozvadecCabinetGrid | null {
  const type = getRozvadecCabinetType(id);
  if (!type || type.kind !== 'address' || type.sekce.length === 0) return null;
  return { typeId: type.id, kapacita: type.kapacita, sekce: type.sekce };
}

/** Row-major cells (řádek × sloupec) across sections — only `kind === 'address'`. */
export function cabinetGridCellsForType(id: string | null | undefined): RozvadecCabinetGridCell[] | null {
  const grid = cabinetGridForType(id);
  if (!grid) return null;
  const cells: RozvadecCabinetGridCell[] = [];
  let globalIndex = 0;
  grid.sekce.forEach((section, sectionIndex) => {
    for (let row = 0; row < section.radky; row += 1) {
      for (let col = 0; col < section.sloupce; col += 1) {
        cells.push({
          sectionId: section.id,
          sectionIndex,
          slotIndex: row * section.sloupce + col,
          row,
          col,
          globalIndex,
        });
        globalIndex += 1;
      }
    }
  });
  return cells;
}

export function cabinetSectionSlotCount(section: Pick<RozvadecCabinetSection, 'sloupce' | 'radky'>): number {
  return section.sloupce * section.radky;
}

/** Cassette rack for a cabinet type. Null = no rack (loose terminations only). */
export function cabinetCassetteRackForType(
  id: string | null | undefined,
): RozvadecCabinetCassetteRack | null {
  return getRozvadecCabinetType(id)?.cassetteRack ?? null;
}

export function cabinetCassetteRackSection(
  rack: RozvadecCabinetCassetteRack | null | undefined,
  sectionId: string | null | undefined,
): RozvadecCabinetCassetteSection | null {
  const key = (sectionId ?? '').trim();
  if (!rack || !key) return null;
  return rack.sections.find((section) => section.id === key) ?? null;
}
